import {
  Card,
  CardContent,
  DialogContentText,
  makeStyles,
  Switch,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import InfoIcon from "@material-ui/icons/Info";
import StarIcon from "@material-ui/icons/Star";
import ActionDialog from "@saleor/components/ActionDialog";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import FormSpacer from "@saleor/components/FormSpacer";
import Grids from "@saleor/components/Grid";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SingleAutocompleteSelectField, {
  SingleAutocompleteChoiceType
} from "@saleor/components/SingleAutocompleteSelectField";
import { ChangeEvent } from "@saleor/hooks/useForm";
import useUser from "@saleor/hooks/useUser";
import { FetchMoreProps } from "@saleor/types";
import { SmopStatusEnum, SmopUserTypeCode } from "@saleor/types/globalTypes";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import StoreBannerImage from "../StoreBannerImage";
import { StoreCreatePageFormData } from "../StoreCreatePage";
import StoreFormFeatureCheckbox from "../StoreFormFeatureCheckbox";
import StoreFormInfoTextTypography from "../StoreFormInfoTextTypography";

const useStyles = makeStyles(
  theme => ({
    account: {
      color: "#0937f4 !important",
      fontSize: 17,
      fontWeight: "bold"
    },
    disabledField: {
      pointerEvents: "none"
    },
    gridContainer: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    },
    textfieldPadding: {
      rightPadding: 4
    }
  }),
  { name: "StoreForm" }
);

export interface StoreFormProps {
  /** Form data & state */
  data: StoreCreatePageFormData;
  change: any;
  submit: any;
  hasChange: any;
  disable: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  discardButton: any;

  /** Errors */
  error: {
    acctName: null;
    acctNum: null;
    bankName: null;
    cluster: null;
    contractId: null;
    designation: null;
    financeName: null;
    financeEmailAddress: null;
    financeMobileNum: null;
    ipay88SubMid: null;
    mallBranch: null;
    authorizeEmailAddress: null;
    authorizeMobileNum: null;
    paymentFeeRate: null;
    promoRibbonDescription: null;
    promoRibbonEnd: null;
    promoRibbonStart: null;
    rentalType: null;
    sellerBrand: null;
    signatoryName: null;
    location: null;
    subTenantType: null;
  };

  /** Display values */
  clusterDisplayValue: string;
  paymentRateDisplay: string;
  rentTypeDisplay: string;
  subTenantTypeDisplay: string;
  sellerDisplayValue: string;
  mallDisplayValue: string;

  /** Autocomplete data */
  clusters: SingleAutocompleteChoiceType[];
  brands: SingleAutocompleteChoiceType[];
  malls: SingleAutocompleteChoiceType[];
  paymentRateChoices: SingleAutocompleteChoiceType[];
  rentTypeChoices: SingleAutocompleteChoiceType[];
  subTenantChoices: SingleAutocompleteChoiceType[];

  /** Fetch actions */
  fetchMalls: (query: string) => void;
  fetchBrands: (query: string) => void;
  fetchClusters: (query: string) => void;
  fetchMoreMalls?: FetchMoreProps;
  fetchMoreBrands?: FetchMoreProps;
  fetchMoreClusters?: FetchMoreProps;

  /** Event handlers */
  onImageUpload?: (file: File) => void;
  onClusterChange: (event: ChangeEvent) => void;
  onBrandChangeStoreType(event: React.ChangeEvent<any>): void;
  onBrandChange(event: React.ChangeEvent<any>): void;
  onMallChange(event: React.ChangeEvent<any>): void;
  onPaymentChange(event: React.ChangeEvent<any>): void;
  onRentalChange(event: React.ChangeEvent<any>): void;
  onSubTenantTypeChange(event: ChangeEvent<any>): void;
}

const StoreForm: React.FC<StoreFormProps> = props => {
  const {
    /** Form data & state */
    data: formData,
    change,
    submit,
    hasChange,
    disable,
    saveButtonBarState,
    discardButton,

    /** Errors */
    error,

    /** Display values */
    clusterDisplayValue,
    paymentRateDisplay,
    rentTypeDisplay,
    subTenantTypeDisplay,
    sellerDisplayValue,
    mallDisplayValue,

    /** Autocomplete data */
    clusters,
    brands,
    malls,
    paymentRateChoices,
    rentTypeChoices,
    subTenantChoices,

    /** Fetch actions */
    fetchMalls,
    fetchBrands,
    fetchClusters,
    fetchMoreMalls,
    fetchMoreBrands,
    fetchMoreClusters,

    /** Event handlers */
    onImageUpload,
    onClusterChange,
    onBrandChangeStoreType,
    onBrandChange,
    onMallChange,
    onPaymentChange,
    onRentalChange,
    onSubTenantTypeChange
  } = props;
  const classes = useStyles({});
  const intl = useIntl();
  const { user } = useUser();
  const theme = useTheme();
  const mediumUpwards = useMediaQuery(theme.breakpoints.up("md"));

  const [statusModal, setModalStatus] = useState(false);

  const isFnB = formData.storeType === "FOOD_AND_BEVERAGES";

  const handleIsActive = value => {
    if (value === "ACT") {
      return true;
    } else {
      return false;
    }
  };

  const handleOnChangeIsActive = value => {
    if (value) {
      return SmopStatusEnum.ACT;
    } else {
      return SmopStatusEnum.INA;
    }
  };

  const handleIsOtherBusiness = value => {
    if (value === "true" || value === true) {
      return true;
    } else {
      formData.bankName = "";
      formData.acctName = "";
      formData.acctNum = "";

      formData.financeName = "";
      formData.financeMobileNum = "";
      formData.financeEmailAddress = "";
      return false;
    }
  };

  return (
    <>
      <Grids>
        <div>
          <Card>
            <CardTitle
              title={intl.formatMessage({
                defaultMessage: "Store Information",
                description: "dialog header"
              })}
              toolbar={
                <>
                  {(user.userType.code === SmopUserTypeCode.ACQUISITION ||
                    user.userType.code === SmopUserTypeCode.OMI_ADMIN ||
                    user.userType.code === "") && (
                    <FormControlLabel
                      control={
                        <StoreFormFeatureCheckbox
                          id="isFeaturedCheckbox"
                          icon={<StarIcon />}
                          checkedIcon={<StarIcon />}
                          name="isFeatured"
                          color="primary"
                          checked={formData.isFeatured}
                          onChange={e => {
                            change({
                              target: {
                                name: e.target.name,
                                value: e.target.checked
                              }
                            });
                          }}
                          disabled={disable}
                        />
                      }
                      label="Feature Shop"
                      labelPlacement="end"
                    />
                  )}
                </>
              }
            />
            <CardContent>
              <SingleAutocompleteSelectField
                idInputTxt="sellerBrandTxt"
                label={intl.formatMessage({
                  defaultMessage: "Seller (Brand)*"
                })}
                name="sellerBrand"
                displayValue={sellerDisplayValue}
                choices={brands}
                disabled={false}
                value={formData.sellerBrand}
                onChange={event => {
                  onBrandChange(event);
                  onBrandChangeStoreType(event);
                }}
                error={!!error.sellerBrand}
                helperText={error.sellerBrand}
                fetchChoices={fetchBrands}
                {...fetchMoreBrands}
              />
              {isFnB ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      id="isUseTenantClusterCheckBox"
                      color="primary"
                      name="isUseTenantCluster"
                      checked={formData.isUseTenantCluster}
                      onChange={e => {
                        if (e.target.checked) {
                          onClusterChange({
                            target: {
                              name: "cluster",
                              value: ""
                            }
                          });
                        }
                        change({
                          target: {
                            name: e.target.name,
                            value: e.target.checked
                          }
                        });
                      }}
                    />
                  }
                  label="Define different Cluster of Store"
                  labelPlacement="end"
                />
              ) : (
                ""
              )}
              <FormSpacer />
              {isFnB && formData.isUseTenantCluster && (
                <SingleAutocompleteSelectField
                  label={intl.formatMessage({
                    defaultMessage: "Cluster"
                  })}
                  name="cluster"
                  displayValue={
                    !formData.isUseTenantCluster ? "" : clusterDisplayValue
                  }
                  choices={clusters}
                  error={!!error.cluster}
                  helperText={error.cluster}
                  disabled={!isFnB || !formData.isUseTenantCluster}
                  value={formData.isUseTenantCluster ? formData.cluster : ""}
                  onChange={onClusterChange}
                  fetchChoices={fetchClusters}
                  {...fetchMoreClusters}
                />
              )}
              <FormSpacer />
              <div className={classes.gridContainer}>
                <SingleAutocompleteSelectField
                  idInputTxt="mallsBranchTxt"
                  label={intl.formatMessage({
                    defaultMessage: "Mall*"
                  })}
                  disabled={false}
                  displayValue={mallDisplayValue}
                  onChange={onMallChange}
                  choices={malls}
                  name="mallBranch"
                  value={formData.mallBranch}
                  error={!!error.mallBranch}
                  helperText={error.mallBranch}
                  fetchChoices={fetchMalls}
                  className={classes.textfieldPadding}
                  {...fetchMoreMalls}
                />
                <SingleAutocompleteSelectField
                  idInputTxt="storeClassificationTxt"
                  label={intl.formatMessage({
                    defaultMessage: "Store Classification"
                  })}
                  disabled={disable || !isFnB}
                  onChange={onSubTenantTypeChange}
                  choices={subTenantChoices}
                  name="subTenantType"
                  value={isFnB ? formData.subTenantType || "" : ""}
                  displayValue={isFnB ? subTenantTypeDisplay : ""}
                  error={!!error.subTenantType}
                  helperText={error.subTenantType}
                  InputProps={{
                    autoComplete: "off"
                  }}
                />
              </div>
              <FormSpacer />
              <TextField
                id="storeLocationTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Store location"
                })}
                name="location"
                onChange={change}
                value={formData.location || ""}
                error={!!error.location}
                helperText={error.location}
                InputProps={{
                  autoComplete: "off"
                }}
                className={classes.textfieldPadding}
              />
              <FormSpacer />
              <TextField
                id="contractIDTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Contract ID*"
                })}
                name="contractId"
                onChange={change}
                value={formData.contractId}
                error={!!error.contractId}
                helperText={error.contractId}
              />
              <FormSpacer />
              <SingleAutocompleteSelectField
                idInputTxt="paymentFeeRateTxt"
                displayValue={paymentRateDisplay}
                label={intl.formatMessage({
                  defaultMessage: "Payment Fee Rate*"
                })}
                disabled={false}
                onChange={onPaymentChange}
                choices={paymentRateChoices}
                name="paymentFeeRate"
                value={formData.paymentFeeRate}
                error={!!error.paymentFeeRate}
                helperText={error.paymentFeeRate}
              />
              <FormSpacer />
              <SingleAutocompleteSelectField
                idInputTxt="rentalTypeTxt"
                displayValue={rentTypeDisplay}
                label={intl.formatMessage({
                  defaultMessage: "Rental Type"
                })}
                disabled={false}
                onChange={onRentalChange}
                choices={rentTypeChoices}
                name="rentalType"
                value={formData.rentalType}
                error={!!error.rentalType}
                helperText={error.rentalType}
              />
              <FormSpacer />
              <TextField
                id="subMIDTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Sub MID"
                })}
                name="ipay88SubMid"
                onChange={change}
                value={formData.ipay88SubMid}
                error={!!error.ipay88SubMid}
                helperText={error.ipay88SubMid}
              />
              <FormSpacer />
              <TextField
                id="tinTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "TIN"
                })}
                name="tinNo"
                onChange={change}
                value={formData.tinNo || ""}
                onKeyPress={event => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                InputProps={{
                  autoComplete: "off",
                  readOnly:
                    user.userType.code === SmopUserTypeCode.SELLER_MASTER ||
                    user.userType.code === SmopUserTypeCode.STORE_ADMIN
                }}
                disabled={
                  disable ||
                  user.userType.code === SmopUserTypeCode.SELLER_MASTER ||
                  user.userType.code === SmopUserTypeCode.STORE_ADMIN
                }
                className={
                  user.userType.code === SmopUserTypeCode.SELLER_MASTER ||
                  user.userType.code === SmopUserTypeCode.STORE_ADMIN
                    ? classes.disabledField
                    : ""
                }
              />
            </CardContent>
          </Card>

          <>
            <StoreBannerImage
              data={formData}
              onChange={change}
              onImageUpload={file => onImageUpload(file)}
              hasManageTenantPermission={true}
            />
            <FormSpacer />
            <Card>
              <CardTitle
                title={intl.formatMessage({
                  defaultMessage: "Store Availability",
                  description: "store availability"
                })}
              />
              <CardContent>
                <Grid container direction="row" spacing={2}>
                  {formData.isActivate === "ACT" ? (
                    <Grid item xs={12} style={{ textAlign: "start" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            id="activeToggle"
                            color="primary"
                            name="isActivate"
                            checked={handleIsActive(formData.isActivate)}
                            onChange={e => {
                              if (!e.target.checked) {
                                setModalStatus(true);
                              } else {
                                change({
                                  target: {
                                    name: e.target.name,
                                    value: handleOnChangeIsActive(
                                      e.target.checked
                                    )
                                  }
                                });
                              }
                            }}
                            disabled={
                              disable ||
                              user.userType.code ===
                                SmopUserTypeCode.STORE_ADMIN
                            }
                          />
                        }
                        label="Active"
                        labelPlacement="end"
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} style={{ textAlign: "start" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            id="activeToggle"
                            color="primary"
                            name="isActivate"
                            checked={handleIsActive(formData.isActivate)}
                            onChange={e => {
                              if (!e.target.checked) {
                                setModalStatus(true);
                              } else {
                                change({
                                  target: {
                                    name: e.target.name,
                                    value: handleOnChangeIsActive(
                                      e.target.checked
                                    )
                                  }
                                });
                              }
                            }}
                            disabled={
                              disable ||
                              user.userType.code ===
                                SmopUserTypeCode.STORE_ADMIN
                            }
                          />
                        }
                        label="Inactive"
                        labelPlacement="end"
                      />
                    </Grid>
                  )}
                  <Grid item xs={6} sm={6}>
                    <TextField
                      id="openingTimeTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Opening Time"
                      })}
                      name="operationStartTime"
                      type="time"
                      onChange={change}
                      value={formData.operationStartTime || ""}
                      InputProps={{
                        autoComplete: "off"
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <TextField
                      id="closingTimeTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Closing Time"
                      })}
                      name="operationEndTime"
                      type="time"
                      onChange={change}
                      value={formData.operationEndTime || ""}
                      InputProps={{
                        autoComplete: "off"
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <FormSpacer />
            {formData.storeType === "" ? null : (
              <>
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Delivery Type",
                      description: "delivery type"
                    })}
                  />
                  <CardContent>
                    {formData.storeType === "FOOD_AND_BEVERAGES" ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="isExpressLongDistanceDeliveryAllowedCheckBox"
                            color="primary"
                            name="isExpressLongDistanceDeliveryAllowed"
                            checked={
                              formData.isExpressLongDistanceDeliveryAllowed
                            }
                            onChange={e => {
                              change({
                                target: {
                                  name: e.target.name,
                                  value: e.target.checked
                                }
                              });
                            }}
                          />
                        }
                        label="Allow Express - Long Distance Delivery"
                        labelPlacement="end"
                      />
                    ) : (
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="isSameDayDeliveryAllowedCheckBox"
                            color="primary"
                            name="isSameDayDeliveryAllowed"
                            checked={formData.isSameDayDeliveryAllowed}
                            onChange={e => {
                              change({
                                target: {
                                  name: e.target.name,
                                  value: e.target.checked
                                }
                              });
                            }}
                          />
                        }
                        label="Same Day Delivery"
                        labelPlacement="end"
                      />
                    )}
                  </CardContent>
                </Card>
                <FormSpacer />
              </>
            )}
          </>
          <FormSpacer />
          <Divider variant="fullWidth" />
          <Card>
            <CardTitle
              title={intl.formatMessage({
                defaultMessage: "Promotional Ribbon",
                description: "Promotional Ribbon"
              })}
            />
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    id="descriptionTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Description"
                    })}
                    name="promoRibbonDescription"
                    value={formData.promoRibbonDescription || ""}
                    onChange={change}
                    error={!!error.promoRibbonDescription}
                    helperText={error.promoRibbonDescription}
                    inputProps={{
                      maxLength: 30
                    }}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    id="startDateTxt"
                    fullWidth
                    label="Start Date"
                    name="promoRibbonStart"
                    type="datetime-local"
                    InputLabelProps={{
                      shrink: true
                    }}
                    value={formData.promoRibbonStart || ""}
                    onChange={e => {
                      change({
                        target: {
                          name: e.target.name,
                          value: e.target.value
                        }
                      });
                    }}
                    onKeyPress={event => {
                      if (event.key) {
                        event.preventDefault();
                        event.stopPropagation();
                      }
                    }}
                    inputProps={{
                      max: formData.promoRibbonEnd,
                      min: Moment(new Date())
                        .local()
                        .format("YYYY-MM-DDTHH:mm")
                    }}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    id="endDateTxt"
                    fullWidth
                    label="End Date"
                    name="promoRibbonEnd"
                    type="datetime-local"
                    value={formData.promoRibbonEnd || ""}
                    onChange={e => {
                      change({
                        target: {
                          name: e.target.name,
                          value: e.target.value
                        }
                      });
                    }}
                    InputLabelProps={{
                      shrink: true
                    }}
                    onKeyPress={event => {
                      if (event.key) {
                        event.preventDefault();
                        event.stopPropagation();
                      }
                    }}
                    inputProps={{
                      min: formData.promoRibbonStart
                        ? formData.promoRibbonStart
                        : Moment(new Date())
                            .local()
                            .format("YYYY-MM-DDTHH:mm")
                    }}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <FormSpacer />
          {mediumUpwards && (
            <Card>
              <CardTitle
                title={intl.formatMessage({
                  defaultMessage: "Authorized Signatory",
                  description: "dialog header"
                })}
              />
              <CardContent>
                <Grids variant="uniform">
                  <div>
                    <TextField
                      id="signatoryNameTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Signatory Name"
                      })}
                      name="signatoryName"
                      value={formData.signatoryName}
                      onChange={change}
                      error={!!error.signatoryName}
                      helperText={error.signatoryName}
                    />
                    <FormSpacer />
                    <TextField
                      id="designationTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Designation"
                      })}
                      name="designation"
                      value={formData.designation}
                      onChange={change}
                      error={!!error.designation}
                      helperText={error.designation}
                    />
                  </div>
                  <div>
                    <TextField
                      id="emailAddressTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Email Address"
                      })}
                      name="authorizeEmailAddress"
                      value={formData.authorizeEmailAddress}
                      onChange={change}
                      error={!!error.authorizeEmailAddress}
                      helperText={error.authorizeEmailAddress}
                    />
                    <FormSpacer />
                    <TextField
                      id="mobileNoTxt"
                      fullWidth
                      label={intl.formatMessage({
                        defaultMessage: "Mobile No."
                      })}
                      name="authorizeMobileNum"
                      value={formData.authorizeMobileNum}
                      onChange={change}
                      error={!!error.authorizeMobileNum}
                      helperText={error.authorizeMobileNum}
                    />
                  </div>
                </Grids>
              </CardContent>
            </Card>
          )}
          {!mediumUpwards && (
            <Card>
              <CardTitle
                title={intl.formatMessage({
                  defaultMessage: "Authorized Signatory",
                  description: "dialog header"
                })}
              />
              <CardContent>
                <TextField
                  id="signatoryNameTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Signatory Name"
                  })}
                  name="signatoryName"
                  value={formData.signatoryName}
                  onChange={change}
                  error={!!error.signatoryName}
                  helperText={error.signatoryName}
                />
                <FormSpacer />
                <TextField
                  id="designationName"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Designation"
                  })}
                  name="designation"
                  value={formData.designation}
                  onChange={change}
                  error={!!error.designation}
                  helperText={error.designation}
                />
                <FormSpacer />
                <TextField
                  id="emailAddressTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Email Address"
                  })}
                  name="authorizeEmailAddress"
                  value={formData.authorizeEmailAddress}
                  onChange={change}
                  error={!!error.authorizeEmailAddress}
                  helperText={error.authorizeEmailAddress}
                />
                <FormSpacer />
                <TextField
                  id="mobileNoTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Mobile No."
                  })}
                  name="authorizeMobileNum"
                  value={formData.authorizeMobileNum}
                  onChange={change}
                  error={!!error.authorizeMobileNum}
                  helperText={error.authorizeMobileNum}
                />
              </CardContent>
            </Card>
          )}
        </div>
        <div>
          {mediumUpwards && (
            <>
              <StoreBannerImage
                data={formData}
                onChange={change}
                onImageUpload={file => onImageUpload(file)}
                hasManageTenantPermission={true}
              />
              <FormSpacer />
              <Card>
                <CardTitle
                  title={intl.formatMessage({
                    defaultMessage: "Store Availability",
                    description: "store availability"
                  })}
                />
                <CardContent>
                  <Grid container direction="row" spacing={2}>
                    {formData.isActivate === "ACT" ? (
                      <Grid item xs={12} style={{ textAlign: "start" }}>
                        <FormControlLabel
                          control={
                            <Switch
                              id="activeToggle"
                              color="primary"
                              name="isActivate"
                              checked={handleIsActive(formData.isActivate)}
                              onChange={e => {
                                if (!e.target.checked) {
                                  setModalStatus(true);
                                } else {
                                  change({
                                    target: {
                                      name: e.target.name,
                                      value: handleOnChangeIsActive(
                                        e.target.checked
                                      )
                                    }
                                  });
                                }
                              }}
                              disabled={
                                disable ||
                                user.userType.code ===
                                  SmopUserTypeCode.STORE_ADMIN
                              }
                            />
                          }
                          label="Active"
                          labelPlacement="end"
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12} style={{ textAlign: "start" }}>
                        <FormControlLabel
                          control={
                            <Switch
                              id="activeToggle"
                              color="primary"
                              name="isActivate"
                              checked={handleIsActive(formData.isActivate)}
                              onChange={e => {
                                if (!e.target.checked) {
                                  setModalStatus(true);
                                } else {
                                  change({
                                    target: {
                                      name: e.target.name,
                                      value: handleOnChangeIsActive(
                                        e.target.checked
                                      )
                                    }
                                  });
                                }
                              }}
                              disabled={
                                disable ||
                                user.userType.code ===
                                  SmopUserTypeCode.STORE_ADMIN
                              }
                            />
                          }
                          label="Inactive"
                          labelPlacement="end"
                        />
                      </Grid>
                    )}
                    <Grid item xs={6} sm={6}>
                      <TextField
                        id="openingTimeTxt"
                        fullWidth
                        label={intl.formatMessage({
                          defaultMessage: "Opening Time"
                        })}
                        name="operationStartTime"
                        type="time"
                        onChange={change}
                        value={formData.operationStartTime || ""}
                        InputProps={{
                          autoComplete: "off"
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6}>
                      <TextField
                        id="closingTimeTxt"
                        fullWidth
                        label={intl.formatMessage({
                          defaultMessage: "Closing Time"
                        })}
                        name="operationEndTime"
                        type="time"
                        onChange={change}
                        value={formData.operationEndTime || ""}
                        InputProps={{
                          autoComplete: "off"
                        }}
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <FormSpacer />
              {formData.storeType === "" ? null : (
                <>
                  <Card>
                    <CardTitle
                      title={intl.formatMessage({
                        defaultMessage: "Delivery Type",
                        description: "delivery type"
                      })}
                    />
                    <CardContent>
                      {formData.storeType === "FOOD_AND_BEVERAGES" ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="isExpressLongDistanceDeliveryAllowedCheckBox"
                              color="primary"
                              name="isExpressLongDistanceDeliveryAllowed"
                              checked={
                                formData.isExpressLongDistanceDeliveryAllowed
                              }
                              onChange={e => {
                                change({
                                  target: {
                                    name: e.target.name,
                                    value: e.target.checked
                                  }
                                });
                              }}
                            />
                          }
                          label="Allow Express - Long Distance Delivery"
                          labelPlacement="end"
                        />
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="isSameDayDeliveryAllowedCheckBox"
                              color="primary"
                              name="isSameDayDeliveryAllowed"
                              checked={formData.isSameDayDeliveryAllowed}
                              onChange={e => {
                                change({
                                  target: {
                                    name: e.target.name,
                                    value: e.target.checked
                                  }
                                });
                              }}
                            />
                          }
                          label="Same Day Delivery"
                          labelPlacement="end"
                        />
                      )}
                    </CardContent>
                  </Card>
                  <FormSpacer />
                </>
              )}
            </>
          )}
          <Card>
            <CardTitle
              title={intl.formatMessage({
                defaultMessage: "Business Details",
                description: "dialog header"
              })}
            />
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormControlLabel
                    id="useOtherBankFinanceDetailsCheckbox"
                    control={
                      <Checkbox
                        color="primary"
                        name="isActivateCompanyDetails"
                        checked={handleIsOtherBusiness(
                          formData.isActivateCompanyDetails
                        )}
                        onChange={e => {
                          change({
                            target: {
                              name: e.target.name,
                              value: e.target.checked
                            }
                          });
                        }}
                      />
                    }
                    label="Use other bank finance details"
                    labelPlacement="end"
                  />

                  <div
                    style={{
                      alignItems: "center",
                      display: "flex",
                      flexWrap: "wrap",
                      marginLeft: "2rem"
                    }}
                  >
                    <InfoIcon />
                    <Typography variant="subtitle1">
                      Check if the Store will use different Bank and Finance
                      against the Seller Master data
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} sm={12}></Grid>
              </Grid>
            </CardContent>
          </Card>
        </div>
      </Grids>
      {/* <Grids>
          <div>

          </div>
          <div>

          </div>
        </Grids> */}
      <FormSpacer />
      <Card>
        <CardTitle
          title={intl.formatMessage({
            defaultMessage: "Accounting Details",
            description: "dialog header"
          })}
        />
        {!mediumUpwards && (
          <>
            <CardContent>
              <Typography variant="h6">BANK DETAILS</Typography>
              <FormSpacer />
              <TextField
                id="bankNameTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Bank Name"
                })}
                value={formData.bankName}
                name="bankName"
                onChange={change}
                error={!!error.bankName}
                helperText={error.bankName}
                disabled={!formData.isActivateCompanyDetails}
              />
              <FormSpacer />
              <TextField
                id="acctNameTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Acct. Name"
                })}
                value={formData.acctName}
                name="acctName"
                onChange={change}
                error={!!error.acctName}
                helperText={error.acctName}
                disabled={!formData.isActivateCompanyDetails}
              />
              <FormSpacer />
              <TextField
                id="acctNoTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Acct. No."
                })}
                value={formData.acctNum}
                name="acctNum"
                onChange={change}
                error={!!error.acctNum}
                helperText={error.acctNum}
                disabled={!formData.isActivateCompanyDetails}
              />
              <FormSpacer />
              <Typography variant="h6">FINANCE CONTACT DETAILS</Typography>
              <FormSpacer />
              <TextField
                id="financeNameTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Name"
                })}
                value={formData.financeName}
                name="financeName"
                onChange={change}
                error={!!error.financeName}
                helperText={error.financeName}
                disabled={!formData.isActivateCompanyDetails}
              />
              <FormSpacer />
              <TextField
                id="financeEmailAddressTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Email Address"
                })}
                value={formData.financeEmailAddress}
                name="financeEmailAddress"
                onChange={change}
                error={!!error.financeEmailAddress}
                helperText={error.financeEmailAddress}
                disabled={!formData.isActivateCompanyDetails}
              />
              <FormSpacer />
              <TextField
                id="financeMobileNumTxt"
                fullWidth
                label={intl.formatMessage({
                  defaultMessage: "Mobile No."
                })}
                value={formData.financeMobileNum}
                name="financeMobileNum"
                onChange={change}
                error={!!error.financeMobileNum}
                helperText={error.financeMobileNum}
                disabled={!formData.isActivateCompanyDetails}
              />
            </CardContent>
          </>
        )}
        {mediumUpwards && (
          <Grids variant="uniform">
            <div>
              <CardContent>
                <StoreFormInfoTextTypography variant="h6">
                  BANK DETAILS
                </StoreFormInfoTextTypography>
                <FormSpacer />
                <TextField
                  id="bankNameTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Bank Name"
                  })}
                  value={formData.bankName}
                  name="bankName"
                  onChange={change}
                  error={!!error.bankName}
                  helperText={error.bankName}
                  disabled={!formData.isActivateCompanyDetails}
                />
                <FormSpacer />
                <TextField
                  id="acctNameTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Acct. Name"
                  })}
                  value={formData.acctName}
                  name="acctName"
                  onChange={change}
                  error={!!error.acctName}
                  helperText={error.acctName}
                  disabled={!formData.isActivateCompanyDetails}
                />
                <FormSpacer />
                <TextField
                  id="acctNoTxt"
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Acct. No."
                  })}
                  value={formData.acctNum}
                  name="acctNum"
                  onChange={change}
                  error={!!error.acctNum}
                  helperText={error.acctNum}
                  disabled={!formData.isActivateCompanyDetails}
                />
              </CardContent>
            </div>
            <div>
              <CardContent>
                <>
                  <StoreFormInfoTextTypography variant="h6">
                    FINANCE CONTACT DETAILS
                  </StoreFormInfoTextTypography>
                  <FormSpacer />
                  <TextField
                    id="financeNameTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Name"
                    })}
                    value={formData.financeName}
                    name="financeName"
                    onChange={change}
                    error={!!error.financeName}
                    helperText={error.financeName}
                    disabled={!formData.isActivateCompanyDetails}
                  />
                  <FormSpacer />
                  <TextField
                    id="financeEmailAddressTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Email Address"
                    })}
                    value={formData.financeEmailAddress}
                    name="financeEmailAddress"
                    onChange={change}
                    error={!!error.financeEmailAddress}
                    helperText={error.financeEmailAddress}
                    disabled={!formData.isActivateCompanyDetails}
                  />
                  <FormSpacer />
                  <TextField
                    id="financeMobileNumTxt"
                    fullWidth
                    label={intl.formatMessage({
                      defaultMessage: "Mobile No."
                    })}
                    value={formData.financeMobileNum}
                    name="financeMobileNum"
                    onChange={change}
                    error={!!error.financeMobileNum}
                    helperText={error.financeMobileNum}
                    disabled={!formData.isActivateCompanyDetails}
                  />
                </>
              </CardContent>
            </div>
          </Grids>
        )}
      </Card>

      <ActionDialog
        confirmButtonState={"default"}
        onClose={() => {
          setModalStatus(false);
        }}
        onConfirm={() => {
          change({
            target: {
              name: "isActivate",
              value: handleOnChangeIsActive(false)
            }
          });
          setModalStatus(false);
        }}
        open={statusModal}
        title={intl.formatMessage({
          defaultMessage: "Deactivate Store ",
          description: "dialog title"
        })}
        variant="default"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage={"Are you sure you want to set this as Inactive?"}
          />
        </DialogContentText>
      </ActionDialog>

      <SaveButtonBar
        onSave={submit}
        disabled={!hasChange}
        state={saveButtonBarState}
        labels={{ cancel: "", delete: "", save: "" }}
        onCancel={discardButton}
      />
    </>
  );
};

export default StoreForm;
